import { useEffect, useRef, useState } from "react";
import {
  CalendarCheck,
  ContactRound,
  LockKeyhole,
  ShieldCheck,
  X,
} from "lucide-react";
import {
  ACCESS_ROLES,
  effectiveAccess,
  isClinicalProvider,
  roleAccess,
} from "../access/policy";
import PermissionsEditor from "./PermissionsEditor";
const initialProfile = (member) => ({
  nombre: member?.nombre || "",
  email: member?.email || "",
  telefono: member?.telefono || "",
  rol: ACCESS_ROLES[member?.rol] ? member.rol : "doctor",
  atiendePacientes: member ? isClinicalProvider(member) : true,
  nombreAgenda: member?.nombreAgenda || member?.nombre || "",
  especialidad: member?.especialidad || "",
  cedula: member?.cedula || "",
  universidad: member?.universidad || "",
  universidadLogoURL: member?.universidadLogoURL || "",
  universidadLogoBase64: member?.universidadLogoBase64 || "",
  color: member?.color || "#5578cb",
  access: member ? effectiveAccess(member) : roleAccess("doctor"),
});
export default function TeamMemberDialog({
  member,
  clinicName,
  onClose,
  onSave,
  onState,
  canEditPermissions = true,
  canEditProfile = true,
  canSuspend = false,
  busy = false,
  error = "",
}) {
  const [form, setForm] = useState(() => initialProfile(member)),
    [panel, setPanel] = useState("profile"),
    [confirmSuspend, setConfirmSuspend] = useState(false),
    [validation, setValidation] = useState("");
  const dialog = useRef(null),
    previousFocus = useRef(null),
    closeRef = useRef(onClose),
    busyRef = useRef(busy),
    clinicalTouched = useRef(Boolean(member));
  closeRef.current = onClose;
  busyRef.current = busy;
  const owner = member?.isOwner === true,
    isInvitation = Boolean(member?.invitationId),
    existing = Boolean(member?.uid && !isInvitation),
    readOnly = existing && !canEditProfile;
  useEffect(() => {
    previousFocus.current = document.activeElement;
    dialog.current
      ?.querySelector("input:not(:disabled),button:not(:disabled)")
      ?.focus();
    const handle = (event) => {
      if (event.key === "Escape" && !busyRef.current) {
        event.preventDefault();
        closeRef.current();
      }
      if (event.key !== "Tab") return;
      const nodes = [
          ...dialog.current.querySelectorAll(
            "button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled),summary",
          ),
        ].filter((el) => el.getClientRects().length),
        first = nodes[0],
        last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener("keydown", handle);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handle);
      document.body.style.overflow = previousOverflow;
      previousFocus.current?.focus?.();
    };
  }, []);
  useEffect(() => {
    if (error) setPanel("profile");
  }, [error]);
  useEffect(() => {
    if (validation || error) {
      const body = dialog.current?.querySelector(".team-dialog-content");
      if (body) body.scrollTop = 0;
    }
  }, [validation, error]);
  const set = (key, value) =>
    setForm((before) => ({ ...before, [key]: value }));
  const submit = (event) => {
    event.preventDefault();
    if (!form.nombre.trim() || !/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      setValidation("Escribe un nombre y un correo válidos.");
      setPanel("profile");
      return;
    }
    setValidation("");
    onSave({
      ...form,
      nombre: form.nombre.trim(),
      email: form.email.trim().toLowerCase(),
    });
  };
  return (
    <div className="team-modal-backdrop">
      <form
        noValidate
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="team-dialog-title"
        className="team-dialog"
        onSubmit={submit}
      >
        <header className="team-dialog-heading">
          <div>
            <h2 id="team-dialog-title">
              {existing
                ? "Editar a " + member.nombre
                : isInvitation
                  ? "Reenviar invitación"
                  : "Agregar usuario"}
            </h2>
            <p>
              {clinicName}
              {owner
                ? " · Propietaria/o y equipo de atención"
                : " · Perfil, agenda y permisos"}
            </p>
          </div>
          <button
            type="button"
            className="team-icon-button"
            aria-label="Cerrar formulario"
            disabled={busy}
            onClick={onClose}
          >
            <X size={19} />
          </button>
        </header>
        <div
          className="team-dialog-tabs"
          role="tablist"
          aria-label="Configuración del usuario"
        >
          <button
            type="button"
            role="tab"
            id="team-profile-tab"
            aria-selected={panel === "profile"}
            aria-controls="team-profile-panel"
            onClick={() => setPanel("profile")}
          >
            <ContactRound size={16} />
            Perfil y agenda
          </button>
          <button
            type="button"
            role="tab"
            id="team-access-tab"
            aria-selected={panel === "access"}
            aria-controls="team-access-panel"
            onClick={() => setPanel("access")}
          >
            <ShieldCheck size={16} />
            Permisos
          </button>
        </div>
        <div className="team-dialog-content">
          {(error || validation) && (
            <div role="alert" className="team-error">
              {error || validation}
            </div>
          )}
          <section
            id="team-profile-panel"
            role="tabpanel"
            aria-labelledby="team-profile-tab"
            hidden={panel !== "profile"}
          >
            <div className="team-fields">
              <label>
                <span>Nombre en {clinicName}</span>
                <input
                  aria-label="Nombre del usuario"
                  autoComplete="off"
                  required
                  maxLength={100}
                  value={form.nombre}
                  disabled={busy || readOnly}
                  onChange={(e) => set("nombre", e.target.value)}
                />
              </label>
              <label>
                <span>Correo electrónico</span>
                <input
                  type="email"
                  autoComplete="off"
                  required
                  maxLength={254}
                  value={form.email}
                  disabled={busy || existing || isInvitation}
                  onChange={(e) => set("email", e.target.value)}
                />
              </label>
              <label>
                <span>
                  Teléfono <small>Opcional</small>
                </span>
                <input
                  type="tel"
                  maxLength={40}
                  value={form.telefono}
                  disabled={busy || readOnly}
                  onChange={(e) => set("telefono", e.target.value)}
                />
              </label>
              <label>
                <span>Perfil de acceso</span>
                <select
                  aria-label="Perfil de acceso"
                  value={form.rol}
                  disabled={busy || owner || (existing && !canEditPermissions)}
                  onChange={(e) => {
                    const rol = e.target.value;
                    setForm((before) => ({
                      ...before,
                      rol,
                      access: roleAccess(rol),
                      atiendePacientes: clinicalTouched.current
                        ? before.atiendePacientes
                        : rol === "doctor",
                    }));
                  }}
                >
                  {Object.entries(ACCESS_ROLES).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <p className="team-help">
              {owner
                ? "La propiedad y el acceso administrativo se conservan."
                : "El perfil sugiere permisos iniciales; puedes personalizarlos en la otra pestaña."}
            </p>
            <section className="team-clinical-section">
              <label className="team-switch-row">
                <span>
                  <strong>Atiende pacientes</strong>
                  <small>Aparece como profesional al agendar citas.</small>
                </span>
                <input
                  className="team-switch"
                  type="checkbox"
                  aria-label="Atiende pacientes"
                  checked={form.atiendePacientes}
                  disabled={busy || readOnly}
                  onChange={(e) => {
                    clinicalTouched.current = true;
                    set("atiendePacientes", e.target.checked);
                  }}
                />
              </label>
              {form.atiendePacientes && (
                <div className="team-clinical-fields">
                  <div className="team-fields">
                    <label>
                      Nombre en agenda
                      <input
                        maxLength={100}
                        value={form.nombreAgenda}
                        placeholder={form.nombre || "Nombre del profesional"}
                        disabled={busy || readOnly}
                        onChange={(e) => set("nombreAgenda", e.target.value)}
                      />
                    </label>
                    <label>
                      Especialidad
                      <input
                        maxLength={120}
                        value={form.especialidad}
                        placeholder="Ej. Odontología general"
                        disabled={busy || readOnly}
                        onChange={(e) => set("especialidad", e.target.value)}
                      />
                    </label>
                    <label>
                      Color en agenda
                      <span className="team-color-field">
                        <input
                          type="color"
                          aria-label="Color en agenda"
                          value={form.color}
                          disabled={busy || readOnly}
                          onChange={(e) => set("color", e.target.value)}
                        />
                        <small>Identifica sus citas</small>
                      </span>
                    </label>
                  </div>
                  <p className="team-agenda-preview">
                    <CalendarCheck size={16} />
                    {form.nombreAgenda || form.nombre || "Este integrante"}{" "}
                    {existing
                      ? "estará disponible en la agenda."
                      : "aparecerá al aceptar la invitación."}
                  </p>
                </div>
              )}
            </section>
            <details className="team-professional-details">
              <summary>Datos profesionales adicionales</summary>
              <div className="team-fields">
                <label>
                  Cédula profesional
                  <input
                    maxLength={40}
                    value={form.cedula}
                    disabled={busy || readOnly}
                    onChange={(e) => set("cedula", e.target.value)}
                  />
                </label>
                <label>
                  Universidad
                  <input
                    maxLength={120}
                    value={form.universidad}
                    disabled={busy || readOnly}
                    onChange={(e) => set("universidad", e.target.value)}
                  />
                </label>
              </div>
            </details>
            {!existing && (
              <p className="team-help">
                Enviaremos un enlace seguro al correo. La persona acepta con su
                cuenta y sus accesos a otros consultorios se conservan.
              </p>
            )}
          </section>
          <section
            id="team-access-panel"
            role="tabpanel"
            aria-labelledby="team-access-tab"
            hidden={panel !== "access"}
          >
            <PermissionsEditor
              value={form.access}
              role={form.rol}
              owner={owner}
              disabled={busy || !canEditPermissions}
              onChange={(access) => set("access", access)}
            />
          </section>
          {confirmSuspend && (
            <div className="team-state-confirm">
              <strong>¿Desactivar el acceso a {clinicName}?</strong>
              <p>
                Se conservarán sus citas y registros. Sus otros consultorios
                seguirán iguales.
              </p>
              <div className="team-actions">
                <button
                  type="button"
                  className="team-button team-danger"
                  disabled={busy}
                  onClick={() => onState(false)}
                >
                  Desactivar acceso
                </button>
                <button
                  type="button"
                  className="team-button"
                  disabled={busy}
                  onClick={() => setConfirmSuspend(false)}
                >
                  Conservar acceso
                </button>
              </div>
            </div>
          )}
        </div>
        <footer className="team-dialog-footer">
          {owner ? (
            <span className="team-protected">
              <LockKeyhole size={14} />
              Propietaria/o protegido
            </span>
          ) : existing && canSuspend ? (
            <button
              type="button"
              className="team-button team-quiet team-danger"
              disabled={busy}
              onClick={() =>
                member.activo === false
                  ? onState(true)
                  : setConfirmSuspend(true)
              }
            >
              {member.activo === false
                ? "Reactivar acceso"
                : "Desactivar acceso"}
            </button>
          ) : (
            <span />
          )}
          <div className="team-actions">
            <button
              type="button"
              className="team-button"
              disabled={busy}
              onClick={onClose}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="team-button team-primary"
              disabled={
                busy || (existing && !canEditProfile && !canEditPermissions)
              }
            >
              {busy
                ? "Guardando…"
                : existing
                  ? "Guardar cambios"
                  : isInvitation
                    ? "Reenviar invitación"
                    : "Enviar invitación"}
            </button>
          </div>
        </footer>
      </form>
    </div>
  );
}
